import React from 'react';
import {
  Modal,
  M,
  H4,
  IconButton,
  Button,
  S,
  Flex,
} from 'design-system-web';
import styles from './RemoveCompanyRoleModal.scss';
import { FONT_AWESOME_REGULAR } from 'lane-shared/helpers/constants/icons';
import { CircleListView, Label, MultiselectField } from 'components';
import { useTranslation } from 'react-i18next';
import { ApolloQueryResult, OperationVariables } from '@apollo/client';
import { useRemoveCompanyRole } from './useRemoveCompanyRole';
import { CompanyRole } from './types';

type Props = {
  isModalOpen: boolean;
  setModalClose: (value: boolean) => void;
  selectedUser: {
    id: string;
    name: string;
    email: string;
    companyRoles: CompanyRole[];
  };
  channelId: string;
  refetch: (
    variables?: Partial<OperationVariables> | undefined
  ) => Promise<ApolloQueryResult<unknown>>;
};

export const RemoveCompanyRoleModal = ({
  isModalOpen,
  setModalClose,
  selectedUser,
  channelId,
  refetch,
}: Props) => {
  const { t } = useTranslation();

  const {
    loading,
    rolesError,
    selectedRoles,
    roleOptions,
    affectedTenants,
    hasNoRoles,
    isButtonDisabled,
    onCloseModal,
    handleSelectRoles,
    handleRemoveRoles,
  } = useRemoveCompanyRole({
    selectedUser,
    setModalClose,
    refetch,
    channelId,
    isModalOpen,
  });

  // Handle query errors
  React.useEffect(() => {
    if (rolesError) {
      window.Alert.alert({
        title: t('web.admin.channel.users.all.table.removeCompanyRole.error'),
        error: rolesError,
      });
    }
  }, [rolesError, t]);

  return (
    <Modal
      className={styles.RemoveCompanyRoleModal}
      isOpen={isModalOpen}
      onClose={onCloseModal}
      isCloseButtonHidden
    >
      <Flex justify="space-between" align="center">
        <H4>{t('web.admin.channel.users.all.table.removeCompanyRole.title')}</H4>
        <IconButton
          type={FONT_AWESOME_REGULAR}
          onClick={onCloseModal}
          icon="times"
          size="small"
        />
      </Flex>

      <CircleListView
        image={null}
        logo={null}
        name={selectedUser.name}
        className={styles.circleListView}
      >
        <M>{`${selectedUser.name} (${selectedUser.email})`}</M>
      </CircleListView>

      {hasNoRoles ? (
        <S className={styles.warningText}>
          {t('web.admin.channel.users.all.table.removeCompanyRole.noRoles')}
        </S>
      ) : (
        <>
          <Label>
            {t('web.admin.channel.users.all.table.removeCompanyRole.selectRole')}
          </Label>
          <MultiselectField
            testId="rolesSelector"
            items={roleOptions}
            isLoading={loading}
            disabled={loading}
            value={selectedRoles}
            onChange={handleSelectRoles}
            isFullWidth
            isClearable={false}
          />

          {affectedTenants.length > 0 && (
            <S className={styles.infoText}>
              {t('web.admin.channel.users.all.table.removeCompanyRole.affectedTenants', {
                tenants: affectedTenants.join(', '),
              })}
            </S>
          )}

          <S className={styles.infoText}>
            {t('web.admin.channel.users.all.table.removeCompanyRole.info')}
          </S>
        </>
      )}

      <div className={styles.divider} />

      <Flex>
        <Button
          variant="primary"
          size="large"
          className={styles.removeButton}
          onClick={handleRemoveRoles}
          disabled={isButtonDisabled}
        >
          {selectedRoles.length > 0
            ? `${t('web.admin.channel.users.all.table.removeCompanyRole.remove')} (${selectedRoles.length})`
            : t('web.admin.channel.users.all.table.removeCompanyRole.remove')}
        </Button>
        <Button variant="secondary" size="large" onClick={onCloseModal}>
          {t('web.admin.channel.users.all.table.removeCompanyRole.cancel')}
        </Button>
      </Flex>
    </Modal>
  );
};
